import { BaseTextField, ControlledTextField, SectionTitle } from 'modules/shared/components';

/**
 * ScoredInterviewRound
 *
 * Renders a scored evaluation section for an interview round.
 * Each criterion captures a numeric score and a textual remark,
 * and contributes to a computed total score.
 *
 * This component is form-bound but form-agnostic:
 * it assumes react-hook-form context is already provided.
 *
 * Props:
 * @param {string} title - Section title (e.g. "ROUND 1 - APTITUDE").
 * @param {string} roundKey - Root form key for the round (e.g. "round1").
 * @param {Array<{ key: string, label: string, max: number }>} criteria
 *   List of scoring criteria and their maximum values.
 * @param {boolean} readOnly - Disables all inputs when true.
 * @param {number} totalScore - Precomputed total score for the round.
 *
 * Notes:
 * - Form field names are derived using the pattern:
 *   `${roundKey}.${criterion.key}.score` and `.remark`
 * - Total score is displayed as a read-only derived value.
 */
export default function ScoredInterviewRound({ title, roundKey, criteria, readOnly, totalScore }) {
  const maxTotalScore = criteria.reduce((sum, { max }) => sum + max, 0);

  return (
    <div className='column gap-4'>
      <SectionTitle
        title={title}
        action={<span className='font-medium'>Candidate Test Score</span>}
      />

      <div className='column gap-1'>
        {criteria.map((item) => (
          <div key={item.key} className='grid grid--3-cols'>
            <span>{item.label}</span>
            <div className='row gap-1 row--center'>
              <ControlledTextField
                id={`${roundKey}.${item.key}.score`}
                name={`${roundKey}.${item.key}.score`}
                type='number'
                inputMode='numeric'
                hideArrow
                disabled={readOnly}
                rules={{
                  required: 'Required',
                  max: { value: item.max, message: `Max: ${item.max}` },
                  min: { value: 0, message: 'Min: 0' },
                }}
                placeholder=''
                sx={{ width: 100 }}
                wrapperSx={{ width: 'auto' }}
                showErrorInline
                reserveErrorSpace
                suffix={`/ ${item.max}`}
              />
            </div>
            <ControlledTextField
              id={`${roundKey}.${item.key}.remark`}
              name={`${roundKey}.${item.key}.remark`}
              type='text'
              disabled={readOnly}
              rules={{
                required: 'Remark is required',
                maxLength: { value: 100, message: 'Max 100 characters' },
              }}
              placeholder='Enter remark'
              showErrorInline
              reserveErrorSpace
            />
          </div>
        ))}
        <div className='grid grid--3-cols'>
          <span className='font-medium'>Total Score</span>
          <div className='row gap-1 row--center'>
            <BaseTextField
              id={`${roundKey}.total_score`}
              onChange={() => {}}
              type='number'
              disabled={true}
              value={totalScore}
              wrapperSx={{ width: 'auto' }}
              suffix={`/ ${maxTotalScore}`}
              showErrorInline
              reserveErrorSpace
              sx={{
                width: 100,
                '& .MuiOutlinedInput-root.Mui-disabled': {
                  pointerEvents: 'none',
                  cursor: 'default',
                },
              }}
            />
          </div>
          <ControlledTextField
            id={`${roundKey}.remark`}
            type='text'
            name={`${roundKey}.remark`}
            disabled={readOnly}
            rules={{
              required: 'Final remark is required',
              maxLength: { value: 100, message: 'Max 100 characters' },
            }}
            placeholder='Enter remark'
            showErrorInline
            reserveErrorSpace
          />
        </div>
      </div>
    </div>
  );
}
